import { Modal } from "components";
import { FieldGroup } from "components";
import { useIsSubmitting } from "hooks/useIsSubmitting";
import { EmployeeModel } from "models";
import { useState } from "react";
import { useSubmit } from "react-router";
import { Button, Form, Select } from "semantic-ui-react";
import { useAuth, useUI } from "services";
import { USER_MSG, getTranslatedUserMsg } from "strings";
import { useT } from "i18n";
import { EMPLOYEE_ACTIONS } from "utils/action-types";

interface AddModalProps {
  isOpen: boolean;
  onClose: () => void;
  companyMappings: { value: string; text: string }[];
  hasEditAccess: boolean;
  hasAccessToCompanyDropdown: boolean;
  isFromCompanyDetailRoute?: boolean;
  dealerId?: string;
}

export default function AddModal({
  isOpen,
  onClose,
  companyMappings,
  hasEditAccess,
  hasAccessToCompanyDropdown,
  isFromCompanyDetailRoute,
  dealerId,
}: AddModalProps) {
  const t = useT();
  const { user } = useAuth();
  const submit = useSubmit();
  const { showError } = useUI();

  const dealerIdToUse = isFromCompanyDetailRoute ? dealerId : user?.dealerId;

  const { isSubmitting } = useIsSubmitting();

  const [employeeModel, setEmployeeModel] = useState(
    EmployeeModel({
      isActive: false,
      ...(isFromCompanyDetailRoute
        ? {
            role: "admin",
            dealerId: dealerIdToUse,
          }
        : {
            role: "",
            dealerId: dealerIdToUse,
          }),
    }),
  );

  const addEmployee = () => {
    if (!employeeModel.isValid()) {
      showError(getTranslatedUserMsg(t, "ERROR_INVALID"));
      return;
    }

    submit(
      {
        actionType: EMPLOYEE_ACTIONS.ADD_EMPLOYEE,
        employee: JSON.stringify(employeeModel.data),
      },
      { method: "post" },
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t("section.employee.newEmployee", "New Employee")}
      actions={
        <div className={"controls"}>
          <Button
            basic
            content={t("component.common.cancel", "Cancel")}
            onClick={onClose}
          />
          {hasEditAccess && (
            <Button
              primary
              content={t("section.employee.submit", "Submit")}
              className={"control-right"}
              disabled={isSubmitting}
              onClick={addEmployee}
            />
          )}
        </div>
      }
    >
      <Form className={"row"} style={{ gap: "22px", justifyContent: "space-evenly" }}>
        <FieldGroup
          fields={"name phone isActive"}
          disabled={!hasEditAccess}
          model={employeeModel}
        />
        <div>
          {hasAccessToCompanyDropdown && (
            <div className={"field"}>
              <label>{t("section.employee.company", "Company")}</label>
              <Select
                search
                placeholder={
                  companyMappings.find(
                    (company: { value: string; text: string }) =>
                      company.value === employeeModel.data.dealerId,
                  )?.text || t("section.employee.selectCompany", "Select Company")
                }
                options={companyMappings}
                onChange={(_, { value }: any) => {
                  setEmployeeModel(
                    EmployeeModel({
                      ...employeeModel.data,
                      dealerId: value,
                    }),
                  );
                }}
                disabled={isFromCompanyDetailRoute}
                value={dealerIdToUse}
                forceSelection={false}
                selectOnBlur={false}
                data-cy={"employee-status-select"}
              />
            </div>
          )}
          <FieldGroup
            fields={"role email"}
            disabled={!hasEditAccess}
            model={employeeModel}
          />
        </div>
      </Form>
    </Modal>
  );
}
